import { useState } from 'react';
import { Alert, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import ClaudeCodeLoginGate from '../../components/claude-code/ClaudeCodeLoginGate';
import EmployeeSessionList from '../../components/claude-code/EmployeeSessionList';
import { HELP_TEXT } from '../../components/claude-code/agentTelemetryHelp';
import { HelpLabel } from '../../components/claude-code/HelpTooltip';
import TopAgentsTable from '../../components/claude-code/TopAgentsTable';
import TopMcpToolsTable from '../../components/claude-code/TopMcpToolsTable';
import TopSkillsTable from '../../components/claude-code/TopSkillsTable';
import { ROUTES } from '../../routes/routes';
import { useEmployeeSessions, useEmployeeSummary } from '../../apps/apis/claudeCodeDashboard/hooks';

const WINDOW_OPTIONS = [7, 14, 30, 60, 90];
const DEFAULT_SESSION_LIMIT = 25;

const ClaudeCodeEmployeeDetailPage = () => {
  const params = useParams<{ employeeId: string }>();
  const employeeId = params.employeeId ?? '';
  const [windowDays, setWindowDays] = useState(30);
  return (
    <ClaudeCodeLoginGate>
      {(session) => (
        <Container fluid className="py-4">
          <div className="d-flex align-items-end justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <Link to={ROUTES.agentTelemetry.overview} className="small text-muted">
                ← Overview
              </Link>
              <h2 className="mb-1 mt-1">{employeeId}</h2>
              <div className="text-muted small">
                Viewing as <strong>{session.email}</strong>
                {session.isAdmin ? <span className="ms-2 badge bg-primary">admin</span> : null}
              </div>
            </div>
            <Form.Group controlId="windowDays" className="d-flex align-items-center gap-2">
              <Form.Label className="mb-0 small text-muted">
                <HelpLabel description={HELP_TEXT.window}>Window</HelpLabel>
              </Form.Label>
              <Form.Select
                size="sm"
                value={windowDays}
                onChange={(e) => setWindowDays(Number(e.target.value))}
                style={{ width: 120 }}
              >
                {WINDOW_OPTIONS.map((days) => (
                  <option key={days} value={days}>
                    {days} days
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <EmployeeBody employeeId={employeeId} windowDays={windowDays} />
        </Container>
      )}
    </ClaudeCodeLoginGate>
  );
};

const EmployeeBody = ({ employeeId, windowDays }: { employeeId: string; windowDays: number }) => {
  const summary = useEmployeeSummary(employeeId, windowDays);
  const sessions = useEmployeeSessions(employeeId, windowDays, DEFAULT_SESSION_LIMIT, 0);

  if (summary.isLoading || sessions.isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }
  if (summary.error) {
    return <Alert variant="danger">Failed to load summary: {(summary.error as Error).message}</Alert>;
  }
  if (sessions.error) {
    return <Alert variant="danger">Failed to load sessions: {(sessions.error as Error).message}</Alert>;
  }
  if (!summary.data || !sessions.data) {
    return null;
  }
  return (
    <>
      <Row className="g-3 mb-3">
        <Col md={3}>
          <Stat label="Sessions" description={HELP_TEXT.sessions} value={summary.data.sessions.toLocaleString()} />
        </Col>
        <Col md={3}>
          <Stat label="Events" description={HELP_TEXT.events} value={summary.data.events.toLocaleString()} />
        </Col>
        <Col md={3}>
          <Stat
            label="Tokens (in / out)"
            description="Input and output tokens reported by the runtime for this employee in the selected window."
            value={`${summary.data.inputTokens.toLocaleString()} / ${summary.data.outputTokens.toLocaleString()}`}
          />
        </Col>
        <Col md={3}>
          <Stat label="Cost (USD)" description={HELP_TEXT.costUsd} value={`$${summary.data.totalCostUsd.toFixed(2)}`} />
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title className="h6">
                <HelpLabel description={HELP_TEXT.topSkills}>Top skills</HelpLabel>
              </Card.Title>
              <TopSkillsTable data={summary.data.topSkills} countLabel="Sessions" />
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title className="h6">
                <HelpLabel description={HELP_TEXT.topAgents}>Top agents</HelpLabel>
              </Card.Title>
              <TopAgentsTable data={summary.data.topAgents} countLabel="Sessions" />
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title className="h6">
                <HelpLabel description={HELP_TEXT.topMcpTools}>Top MCP tools</HelpLabel>
              </Card.Title>
              <TopMcpToolsTable data={summary.data.topMcpTools} countLabel="Sessions" />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Card>
        <Card.Body>
          <Card.Title className="h6">
            <HelpLabel description="Most recent sessions for this employee in the selected window.">
              Recent sessions
            </HelpLabel>
          </Card.Title>
          <EmployeeSessionList sessions={sessions.data.sessions} />
        </Card.Body>
      </Card>
    </>
  );
};

const Stat = ({ label, description, value }: { label: string; description: string; value: string }) => (
  <Card className="h-100">
    <Card.Body>
      <div className="text-muted small">
        <HelpLabel description={description}>{label}</HelpLabel>
      </div>
      <div className="fs-4 fw-semibold">{value}</div>
    </Card.Body>
  </Card>
);

export default ClaudeCodeEmployeeDetailPage;
